<template>
    <div>
        <form ref="form" v-on:submit="searchSubmit" class="advanced-search">
            
            <div class="type-toggle" :class="{'selected': isType('artifact')}">
                <input type="radio" id="artifacts-only" name="type" value="artifact"
                        checked v-model="searchType">
                <label for="artifacts-only" class="h6">{{ $t('labels.artifacts') }}</label>
            </div>

            <div class="type-toggle" :class="{'selected': isType('archive')}">
                <input type="radio" id="archives-only" name="type" value="archive" v-model="searchType">
                <label for="archives-only" class="h6">{{ $t('labels.archives') }}</label>
            </div>
            
            <!-- search filter components -->
            <div class="inputs input-container">
                <p class="label header">{{ $t('labels.search_filters') }}</p>
                <SearchFilters ref="textFilters" :urlFilters="urlQuery"></SearchFilters>
            </div>

            <!-- date input: artifacts -->
            <div class="date inputs input-container" v-show="type == 'artifact'">
                <p class="label header">{{ $t('labels.date') }}</p>
                <div class="inputs">
                    <label for="start-date" class="sr-only">{{ $t('labels.start_date') }}</label>
                    <input name="startDate" id="start-date" type="text" v-model="earliest" maxlength="4"/>
                    <p class="label lowercase">{{ $t('labels.to') }}</p>
                    <label for="end-date" class="sr-only">{{ $t('labels.end_date') }}</label>
                    <input name="endDate" id="end-date" type="text" v-model="latest" maxlength="4" />
                </div>
            </div>

            <!-- date input: archives -->
            <div class="date inputs input-container" v-show="type == 'archive'">
                <p class="label header">{{ $t('labels.date') }}</p>
                <div class="inputs">
                    <label for="text-date" class="sr-only">{{ $t('labels.date') }}</label>
                    <input name="textDate" id="text-date" type="text" v-model="unit_dates"/>
                </div>
            </div>


            <!-- dropdown filter components -->
            <div class="inputs input-container">
                <DropdownFilters ref="dropdownFilters" :searchType="searchType" :urlLimiters="urlLimiters"></DropdownFilters>
            </div>
            <div>
                <button type="submit" class="submit-btn">
                    {{ $t('labels.search_button_label') }}
                </button>
                <button type="button" @click="clearSeach" class="clear-button">
                    {{ $t('labels.clear_all_filters') }}
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SearchFilters from '../components/SearchFilters'
import DropdownFilters from '../components/DropdownFilters'

export default {
    components: {
        SearchFilters,
        DropdownFilters,
    },
    data: function() {
        return {
            urlFilters: [],
            urlQuery: [],
            urlLimiters: [],
            earliest: '',
            latest: '',
            unit_dates: ''
        }
    },
    computed: {
        ...mapGetters({
            'type': 'search/type',
            'sort': 'search/sort',
            'order': 'search/order',
            'query': 'search/query',
            'selected_facets': 'search/selected_facets',
            'all_facets': 'search/facets',
            'results': 'results/list'
        }),
        'searchType': {
            get: function () {
                return this.type;
            },
            set: function (value) {
                this.$store.dispatch('search/type', {type: value});
            }
        }
    },
    watch: {
        'earliest': function () {
            this.printableDate();
        },
        'latest': function () {
            this.printableDate();
        },
        'unit_dates': function () {
            this.printableDate();
        },
    },
    methods: {
        printableDate () {
           let dateObj = {};
           if (this.earliest) {
               dateObj.earliest = this.earliest;
           }
           
           if (this.latest) {
               dateObj.latest = this.latest;
           }

           if (this.unit_dates) {
               dateObj.date = this.unit_dates;
           }

            if (Object.keys(dateObj).length) this.$store.dispatch('results/printable_date', dateObj);
        },
        isType: function(type) {
            return this.searchType == type;
        },
        searchQueryChanged: function(event) {
            event.preventDefault()
            event.stopPropagation()
            this.search(event.target.value);
        },
        clearSeach: function() {
            let form = this.$refs.form;
            let inputs = form.querySelectorAll('input[type="text"');
            inputs.forEach(i => i.value = '');

            this.earliest = ''
            this.latest = ''
            this.unit_dates = ''

            // reseting url query and limiters 
            // triggers resets in StringFilters and DropdownFilters components
            this.urlQuery = [];
            this.urlLimiters = [];
        },
        search: function(data, cb) {
            // runs on form submission and on mount

            this.$store.dispatch('search/update',  {
                locale: this.$i18n.locale,
                type: data.type,
                q: data.terms,
                sort: data.sort || this.sort,
                order: data.order || this.order,
                page: data.page,
                size: data.size,
                urlString: data.urlString,
                view: data.view
            }).then(() => {
                if (cb) cb();
                this.$emit('loaded');
            })
        },
        searchSubmit(event) {
            // only runs on form submission


            event.preventDefault()
            event.stopPropagation()
            let searchData =  new FormData(event.currentTarget);

            let query = this.buildQuery(searchData);
            this.setInitialFacets();

            this.search(query, () => {
                this.$router.push(`/${this.$i18n.messages[this.$i18n.locale].routes.advanced_search}?${query.urlString}`);
            });
        },
        setInitialFacets () {
            this.$store.dispatch('search/resetFacets');
            let dropdownTerms = this.$refs.dropdownFilters.getTerms();
            let facetObjects = dropdownTerms.facetObjects;

            facetObjects.forEach(facet => {
                this.$store.dispatch('search/addFacet', facet)
            });
        },
        buildQuery(data) {
            // build terms string for API
            let type = data.get('type');
            let terms = [];
            let page = 1;
            let size = type == 'artifact' ? 24 : 100;

            // add text input terms
            let textTerms = this.$refs.textFilters.getTerms(type);
            if (textTerms) { terms = terms.concat(textTerms.flat()) }

            // add dates
            let dates = this.createDateQuery(data);
            terms = terms.concat(dates.terms);


            // add dropdownterms
            let dropdownTerms = this.$refs.dropdownFilters.getTerms();
            let dropdownValues = dropdownTerms.apiArray;

            // build string for url
            let view = type == 'artifact' ? 'grid' : 'list';
            let urlString = this.createUrlString({
                type: type,
                textTerms: textTerms,
                dates: dates.dates,
                dropdownTerms: dropdownValues,
                page: page,
                size: size,
                view: view
            })

            return {
                terms: terms,
                type: type,
                page: page,
                size: size,
                view: view,
                urlString: urlString
            }
        },
        createDateQuery (data) {
            let terms = [];
            let type = data.get('type');

            let earliestDate = data.get('startDate');
            let latestDate = data.get('endDate');
            let textDate = data.get('textDate');

            let typeDateMap = {
                'earliest': {
                    'artifact': ['earliest', '_gte'],
                },
                'latest': {
                    'artifact': ['latest', '_lte'],
                },
                'textDate': {
                    'archive': ['unit_dates', '_contains']
                }
            }
            
            let dates = [];
            if (type == 'artifact') {
                if (earliestDate) {
                    let searchArray = typeDateMap['earliest'][type]
                    searchArray.push(earliestDate);
                    terms = terms.concat(searchArray);
                    dates.push(searchArray);
                }
    
                if (latestDate) {
                    let searchArray = typeDateMap['latest'][type];
                    searchArray.push(latestDate);
                    terms = terms.concat(searchArray);
                    dates.push(searchArray);
                }
            } else if (type == 'archive') {
                if (textDate) {
                    let searchArray = typeDateMap['textDate'][type];
                    searchArray.push(textDate);
                    terms = terms.concat(searchArray);
                    dates.push(searchArray);
                }
            }

            return {
                terms: terms, 
                dates: dates
            };
        },
        createUrlString(data) {
            let urlString = `type=${data.type}`;
            data.textTerms.forEach((t, i) => {
                urlString += `&q${i+1}=${t.join('::')}`;
            })

            if (this.type == 'archive' && data.dates[0]) {
                urlString += `&${data.dates[0].join('::')}`
            } else {
                data.dates.forEach(d => {
                    urlString += `&${d[0]}=${d[1]}::${d[2]}`
                })
            }

            data.dropdownTerms.forEach(t => {
                let filter = t[1];
                let option = t[2];
                let str = `&${filter}=${option}`;
                urlString += str;
            });

            let sort = data.sort || 'title';
            let order = data.order || 'asc';
            let view = data.view || 'grid';
            let size = data.size || 24;
            let page = data.page || 1;

            urlString += `&sort=${sort}&order=${order}&view=${view}&size=${size}&page=${page}`
            return urlString;
        }
    },
    async mounted() {
        this.$store.dispatch('search/searchType', 'advanced');

        if (this.type == 'all') {
            this.$store.dispatch('search/type', {type: 'artifact'});
        }

        // populate if has query but no url params 
        // when navigating from general search
        let hasUrlQuery = Object.keys(this.$route.query).length > 0;
        if (this.query && this.query.length && !hasUrlQuery) {
            this.urlQuery = [this.query.join('::')];
        }

        if (hasUrlQuery) {
            // clear facets before adding facets from url
            this.$store.dispatch('search/resetFacets');

            let data = this.$route.query;
            let type = data.type;
            let urlString = this.$route.fullPath.split('?')[1];
            let strFilters = []
            let limiterFilters = []
            let terms = []

            let exclude = ['sort','order','page','view', 'type','size']

            for (let key in data) {
                if (key.includes('q')) {
                    strFilters.push(data[key])
                    terms.push(data[key].split('::'))
                } else if (key == 'earliest' || key == 'latest') {
                    let pieces = data[key].split('::');
                    this[key] = pieces[1];
                    terms.push(key, pieces)
                } else if (key.includes('unit_dates')) {
                    let pieces = key.split('::');
                    this[pieces[0]] = pieces[2];
                    terms.push(pieces[0], [pieces[1], pieces[2]])
                } else if (!exclude.includes(key)){
                    limiterFilters.push({key: key, option: data[key]})
                    data[key].split('::').forEach(f => {
                        this.$store.dispatch('search/addFacet', {
                            name: key,
                            value: f
                        })
                    })

                }
            }

            let imageField = 'media.mime_type';
            if (data[imageField] && !this.selected_facets[imageField]) {
                delete data[imageField];
            }
            let mediaField = 'media';
            if (data[mediaField] && !this.selected_facets[mediaField]) {
                delete data[mediaField];
            }
            if (this.selected_facets[imageField] && !data[imageField]) {
                this.$store.dispatch('search/removeFacet', {
                    name: imageField,
                    value: this.selected_facets[imageField]
                });
            }
            if (this.selected_facets[mediaField] && !data[mediaField]) {
                this.$store.dispatch('search/removeFacet', {
                    name: mediaField,
                    value: this.selected_facets[mediaField]
                });
            }

            this.urlQuery = strFilters;
            this.urlLimiters = limiterFilters;
            this.searchType = type;

            this.search({
                terms: terms.flat(),
                type: type,
                urlString: urlString,
                view: data.view,
                size: data.size,
                order: data.order,
                sort: data.sort,
                page: data.page
            })
        }
    }
}
</script>


<style lang="scss">
/* <style scoped lang="scss"> adding scoped breaks global styles??? */

    form.advanced-search {
        border-top: 1px solid $light-gray1;
        margin-top: $s2;
        padding-top: $s2;

        .label {
            margin-top: 1rem;
            margin-right: .5rem;
            font-weight: normal;
        }

        .type-toggle {
            display: inline-block;
            margin-bottom: $s1;
            position: relative;

            label {
                cursor: pointer;
                display: inline-block;
                margin-right: $s1;
                color: $dark-gray2;
            }
            input {
                opacity: 0;
                position: absolute;
                left: 0;
                width: 100%;

                &:focus {
                    + label {
                        outline: 1px solid $black;
                        outline-offset: 1px;
                    }
                }
            }

            &.selected, &:hover {
                label {
                    text-decoration: underline;
                    text-underline-offset: 5px;
                    color: $dark-gray1;
                }
            }
        } 

        .inputs {
            display: flex;
            align-items: center;
            margin-top: .5rem;

            .label.header {
                width: $label-spacing;
                margin-right: 0;
                align-self: flex-start;
                padding-right: 10px;
                box-sizing: border-box;
                font-size: .9rem;
            }
        }

        .date {
            display: flex;
            margin-bottom: 1rem;

            .label:not(.lowercase) {
                margin-right: 0;
            }

            .label.lowercase {
                margin: 0 .5rem;
                text-transform: unset;
            }
            
            input {
                @include input-styles;
                max-width: 80px;
                text-align: center;
                margin: 0;

                &#text-date {
                    width: 258px;
                    max-width: unset;
                    text-align: left;
                }
            }
        }
        .clear-button {
            background: none;
            border: none;
            font-family: 'Futura';
            text-decoration: underline;
            cursor: pointer;
        }
        .submit-btn {
            cursor: pointer;
            padding: 1.15rem 1.7rem;
            border: solid 2px $dark-gray1;
            background: $dark-gray1;
            color: $white;
            text-transform: uppercase;
            border-radius: 0;
            outline: none;
            margin-left: $label-spacing;
            margin-right: $s2;
            display: inline-block;

            &:hover, &:focus {
                background: darken($dark-gray1, 12%);
            }
        }
    }

    @media (max-width: $mq-max-width-desktop) {
        form.advanced-search .submit-btn {
            margin-left: 0;
        }
    }

    @media (max-width: $mq-max-width-desktop-sml) {
        form.advanced-search {
            .inputs.input-container {
                flex-direction: column;
                margin-top: $s1;
    
                .label.header {
                    margin-top: 0;
                    margin-bottom: 0;
                }
    
            }
            .date .inputs {
                align-self: flex-start;

                #text-date {
                    width: 100%;
                }
            }
        }
    }
    @media (max-width: $mq-max-width-mobile) {
        form.advanced-search .date .inputs {
            width: 100%;
        }
    }
</style>